/**
 * Partner detail — `/partners/$partyId` (SPEC screen 2, "360 v1").
 *
 * `GET /parties/{partyId}` is the contract's audited restricted PII read
 * (`partners.party.pii.read`): it is the only operation that returns
 * `tax_registration`, and it is the source of the strong aggregate ETag that
 * validates the header PATCH, both first profile PUTs, and the archive /
 * unarchive transitions. A 412 opens the standard conflict dialog — this
 * screen never silently overwrites someone else's change.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ScrollText } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@erp/ui";

import { ConflictDialog, surfaceProblem } from "@/components/form";
import { formatDate } from "@/lib/format";
import { ApiError } from "@/lib/api/errors";
import { isPreconditionFailed, useIdempotentMutation } from "@/features/shared/ops-api";
import { localizedName } from "@/lib/i18n";

import {
  archiveParty,
  dryRunArchiveParty,
  unarchiveParty,
  updateParty,
  type ArchiveReasonCode,
  type PartyArchiveBlocker,
  type PartyPatch,
} from "../api";
import { partnersKeys, usePartyDetail } from "../queries";
import {
  ALREADY_ARCHIVED_CODE,
  ArchiveDialog,
  NOT_ARCHIVED_CODE,
  RestoreDialog,
  readBlockers,
} from "../components/archive-dialog";
import { AddressesPanel } from "../components/addresses-panel";
import { ChannelIdentitiesPanel } from "../components/channel-identities-panel";
import { ContactsPanel } from "../components/contacts-panel";
import { CustomerProfilePanel } from "../components/customer-profile-panel";
import {
  PartyFormDialog,
  type PartyFormErrors,
  type PartyFormValues,
} from "../components/party-form-dialog";
import { SupplierProfilePanel } from "../components/supplier-profile-panel";

export const PARTY_TABS = ["customer", "supplier", "addresses", "contacts", "channels"] as const;
export type PartyTab = (typeof PARTY_TABS)[number];

export interface PartyDetailPageProps {
  partyId: string;
  tab?: PartyTab;
  onTabChange: (tab: PartyTab) => void;
  onBack: () => void;
  onOpenAuditLog?: () => void;
  canUpdate?: boolean;
  canArchive?: boolean;
  canReadCustomerProfile?: boolean;
  canUpdateCustomerProfile?: boolean;
  canArchiveCustomerProfile?: boolean;
  canReadSupplierProfile?: boolean;
  canUpdateSupplierProfile?: boolean;
  canArchiveSupplierProfile?: boolean;
  canReadAddresses?: boolean;
  canCreateAddresses?: boolean;
  canUpdateAddresses?: boolean;
  canArchiveAddresses?: boolean;
  canReadContacts?: boolean;
  canCreateContacts?: boolean;
  canUpdateContacts?: boolean;
  canArchiveContacts?: boolean;
  canReadChannelIdentities?: boolean;
  canReadCustomerGroups?: boolean;
  canReadSupplierGroups?: boolean;
  canReadPaymentTerms?: boolean;
}

export function PartyDetailPage({
  partyId,
  tab,
  onTabChange,
  onBack,
  onOpenAuditLog,
  canUpdate = true,
  canArchive = true,
  canReadCustomerProfile = true,
  canUpdateCustomerProfile = true,
  canArchiveCustomerProfile = true,
  canReadSupplierProfile = true,
  canUpdateSupplierProfile = true,
  canArchiveSupplierProfile = true,
  canReadAddresses = true,
  canCreateAddresses = true,
  canUpdateAddresses = true,
  canArchiveAddresses = true,
  canReadContacts = true,
  canCreateContacts = true,
  canUpdateContacts = true,
  canArchiveContacts = true,
  canReadChannelIdentities = true,
  canReadCustomerGroups = true,
  canReadSupplierGroups = true,
  canReadPaymentTerms = true,
}: PartyDetailPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const detailQuery = usePartyDetail(partyId);
  const party = detailQuery.data?.data;
  const etag = detailQuery.data?.etag ?? null;

  const [editOpen, setEditOpen] = useState(false);
  const [editErrors, setEditErrors] = useState<PartyFormErrors>({});
  const [conflict, setConflict] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [blockers, setBlockers] = useState<PartyArchiveBlocker[]>([]);
  const [guardError, setGuardError] = useState(false);

  const activeTab: PartyTab = tab ?? "customer";
  const editable = party !== undefined && party.status === "active";

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });

  const updateMutation = useMutation({
    mutationFn: (patch: PartyPatch) => updateParty(partyId, patch, etag ?? ""),
    onSuccess: () => {
      setEditOpen(false);
      setEditErrors({});
      void invalidate();
    },
    onError: (error) => {
      if (isPreconditionFailed(error)) {
        setEditOpen(false);
        setConflict(true);
        return;
      }
      if (error instanceof ApiError && error.code === "tax_registration_invalid") {
        setEditErrors({ taxNumber: error.problem?.detail ?? t("partners.create.taxIncomplete") });
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const dryRunMutation = useIdempotentMutation<unknown, { partyId: string }>({
    mutationFn: (variables, key) => dryRunArchiveParty(variables.partyId, key),
    onSuccess: () => {
      setBlockers([]);
      setGuardError(false);
    },
    onError: (error) => {
      const parsed = readBlockers(error);
      if (parsed.length > 0) {
        setBlockers(parsed);
        setGuardError(false);
        return;
      }
      setGuardError(true);
    },
  });

  const archiveMutation = useIdempotentMutation<
    unknown,
    { partyId: string; reasonCode: ArchiveReasonCode; ifMatch: string }
  >({
    mutationFn: (variables, key) =>
      archiveParty(variables.partyId, variables.reasonCode, variables.ifMatch, key),
    onSuccess: () => {
      setArchiveOpen(false);
      setBlockers([]);
      void invalidate();
    },
    onError: (error) => {
      const parsed = readBlockers(error);
      if (parsed.length > 0) {
        setBlockers(parsed);
        return;
      }
      if (error instanceof ApiError && error.code === ALREADY_ARCHIVED_CODE) {
        setArchiveOpen(false);
        toast({ title: t("partners.archive.alreadyArchived") });
        void invalidate();
        return;
      }
      if (isPreconditionFailed(error)) {
        setArchiveOpen(false);
        setConflict(true);
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const unarchiveMutation = useIdempotentMutation<unknown, { partyId: string; ifMatch: string }>({
    mutationFn: (variables, key) => unarchiveParty(variables.partyId, variables.ifMatch, key),
    onSuccess: () => {
      setRestoreOpen(false);
      void invalidate();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === NOT_ARCHIVED_CODE) {
        setRestoreOpen(false);
        toast({ title: t("partners.archive.notArchived") });
        void invalidate();
        return;
      }
      if (isPreconditionFailed(error)) {
        setRestoreOpen(false);
        setConflict(true);
        return;
      }
      surfaceProblem(error, t);
    },
  });

  // Opening the archive dialog always re-runs the guard: the blocker set is
  // cross-module state that may have changed since the page loaded.
  useEffect(() => {
    if (!archiveOpen) return;
    setBlockers([]);
    setGuardError(false);
    dryRunMutation.mutate({ partyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one preflight per dialog opening
  }, [archiveOpen, partyId]);

  const submitEdit = (values: PartyFormValues): void => {
    setEditErrors({});
    const patch: PartyPatch = {
      display_name: values.displayName,
      default_currency: values.defaultCurrency,
      ...(values.clearTaxRegistration
        ? { tax_registration: null }
        : values.taxCountry !== "" && values.taxNumber !== ""
          ? {
              tax_registration: {
                country_code: values.taxCountry,
                registration_no: values.taxNumber,
              },
            }
          : {}),
    };
    updateMutation.mutate(patch);
  };

  const createdLabel = useMemo(() => {
    if (party === undefined) return "";
    try {
      return formatDate(party.created_at, i18n.language);
    } catch {
      return party.created_at;
    }
  }, [party, i18n.language]);

  if (detailQuery.isPending) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detailQuery.isError || party === undefined) {
    const notFound = detailQuery.error instanceof ApiError && detailQuery.error.status === 404;
    return (
      <div className="flex flex-col gap-4 p-4">
        <Card>
          <CardContent role="alert" className="flex flex-col items-start gap-3 pt-6">
            <p className="text-sm">
              {notFound ? t("partners.detail.notFound") : t("partners.detail.loadError")}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onBack}>
                {t("partners.detail.back")}
              </Button>
              {notFound ? null : (
                <Button type="button" onClick={() => void detailQuery.refetch()}>
                  {t("actions.retry")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto w-fit p-0 text-xs"
            onClick={onBack}
          >
            <ArrowLeft className="size-3 rtl:-scale-x-100" aria-hidden="true" />
            {t("partners.detail.back")}
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 dir="auto" className="text-xl font-semibold">
              {localizedName(party.display_name, party.display_name_ar, i18n.language)}
            </h1>
            <Badge variant={party.kind === "org" ? "secondary" : "outline"}>
              {t(`partners.kind.${party.kind}`)}
            </Badge>
            {party.default_currency !== undefined ? (
              <Badge variant="outline">
                <span dir="ltr">{party.default_currency}</span>
              </Badge>
            ) : null}
            {party.roles.map((role) => (
              <Badge key={role} variant={role === "customer" ? "default" : "secondary"}>
                {t(`partners.role.${role}`)}
              </Badge>
            ))}
            <Badge
              variant={
                party.status === "active"
                  ? "success"
                  : party.status === "archived"
                    ? "secondary"
                    : "outline"
              }
            >
              {t(`partners.status.${party.status}`)}
            </Badge>
          </div>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>
              {t("partners.detail.taxRegistration")}:{" "}
              {party.tax_registration === undefined ? (
                t("partners.detail.noTaxRegistration")
              ) : (
                <span dir="ltr" className="font-mono">
                  {party.tax_registration.country_code} {party.tax_registration.registration_no}
                </span>
              )}
            </span>
            <span>
              {t("partners.detail.country")}:{" "}
              <span dir="ltr" className="font-mono">
                {party.country_code ?? "—"}
              </span>
            </span>
            <span>
              {t("partners.detail.created")}: {createdLabel}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onOpenAuditLog !== undefined ? (
            <Button type="button" variant="ghost" size="sm" onClick={onOpenAuditLog}>
              <ScrollText className="size-4" aria-hidden="true" />
              {t("partners.detail.auditLink")}
            </Button>
          ) : null}
          {canUpdate && editable ? (
            <Button type="button" variant="outline" onClick={() => setEditOpen(true)}>
              {t("partners.detail.editDetails")}
            </Button>
          ) : null}
          {canArchive && party.status === "active" ? (
            <Button type="button" variant="destructive" onClick={() => setArchiveOpen(true)}>
              {t("partners.archive.action")}
            </Button>
          ) : null}
          {canArchive && party.status === "archived" ? (
            <Button type="button" onClick={() => setRestoreOpen(true)}>
              {t("partners.archive.restore")}
            </Button>
          ) : null}
        </div>
      </div>

      {party.status === "archived" ? (
        <p role="status" className="rounded-md border border-warning/40 bg-warning/10 p-2 text-sm">
          {t("partners.detail.archivedBanner")}
        </p>
      ) : null}
      {party.status === "pseudonymized" ? (
        <p
          role="status"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm"
        >
          {t("partners.detail.pseudonymizedBanner")}
        </p>
      ) : null}

      <Tabs value={activeTab} onValueChange={(value) => onTabChange(value as PartyTab)}>
        <TabsList>
          {PARTY_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`partners.detail.tabs.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="customer">
          <CustomerProfilePanel
            partyId={partyId}
            partyEtag={etag}
            {...(party.customer_profile === undefined ? {} : { profile: party.customer_profile })}
            currency={party.default_currency ?? ""}
            partyEditable={editable}
            canRead={canReadCustomerProfile}
            canUpdate={canUpdateCustomerProfile}
            canArchive={canArchiveCustomerProfile}
            canReadGroups={canReadCustomerGroups}
            canReadPaymentTerms={canReadPaymentTerms}
          />
        </TabsContent>

        <TabsContent value="supplier">
          <SupplierProfilePanel
            partyId={partyId}
            partyEtag={etag}
            {...(party.supplier_profile === undefined ? {} : { profile: party.supplier_profile })}
            currency={party.default_currency ?? ""}
            partyEditable={editable}
            canRead={canReadSupplierProfile}
            canUpdate={canUpdateSupplierProfile}
            canArchive={canArchiveSupplierProfile}
            canReadGroups={canReadSupplierGroups}
            canReadPaymentTerms={canReadPaymentTerms}
          />
        </TabsContent>

        <TabsContent value="addresses">
          <AddressesPanel
            partyId={partyId}
            isCustomer={party.roles.includes("customer")}
            partyEditable={editable}
            canRead={canReadAddresses}
            canCreate={canCreateAddresses}
            canUpdate={canUpdateAddresses}
            canArchive={canArchiveAddresses}
          />
        </TabsContent>

        <TabsContent value="contacts">
          <ContactsPanel
            partyId={partyId}
            partyEditable={editable}
            canRead={canReadContacts}
            canCreate={canCreateContacts}
            canUpdate={canUpdateContacts}
            canArchive={canArchiveContacts}
          />
        </TabsContent>

        <TabsContent value="channels">
          <ChannelIdentitiesPanel partyId={partyId} canRead={canReadChannelIdentities} />
        </TabsContent>
      </Tabs>

      <PartyFormDialog
        open={editOpen}
        mode="edit"
        party={party}
        pending={updateMutation.isPending}
        serverErrors={editErrors}
        onSubmit={submitEdit}
        onOpenChange={(open) => {
          if (!open) {
            setEditErrors({});
            setEditOpen(false);
          }
        }}
      />

      <ArchiveDialog
        open={archiveOpen}
        partyName={party.display_name}
        checking={dryRunMutation.isPending}
        blockers={blockers}
        guardError={guardError}
        archiving={archiveMutation.isPending}
        onRetryGuard={() => {
          setGuardError(false);
          dryRunMutation.mutate({ partyId });
        }}
        onConfirm={(reasonCode) =>
          archiveMutation.mutate({ partyId, reasonCode, ifMatch: etag ?? "" })
        }
        onOpenChange={setArchiveOpen}
      />

      <RestoreDialog
        open={restoreOpen}
        partyName={party.display_name}
        pending={unarchiveMutation.isPending}
        onConfirm={() => unarchiveMutation.mutate({ partyId, ifMatch: etag ?? "" })}
        onOpenChange={setRestoreOpen}
      />

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          void invalidate();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
